import React from 'react';
import { motion } from 'motion/react';

/**
 * Colour-block section system.
 * tone:   ink | navy | indigo | teal | plum | slate | light | mint | sky | sand | white
 * pattern: grid | dots | hatch | none
 * glows:  array of { color, top, left, right, bottom, size }
 */
const DARK_TONES = ['ink', 'navy', 'indigo', 'teal', 'plum', 'slate'];

export const isDarkTone = (tone) => DARK_TONES.includes(tone);

export const fadeInUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

export const Section = ({
  tone = 'light',
  pattern = 'none',
  glows = [],
  className = '',
  containerClassName = '',
  padding = 'py-24',
  id,
  rule = true,
  children,
  contentWidth = 1200,
  as: Tag = 'section',
  aurora = false,
  topo = false,
  watermark = false,
  noise = false
}) => {
  const scheme = isDarkTone(tone) ? 'scheme-dark' : 'scheme-light';
  return (
    <Tag
      id={id}
      className={`gx-section tone-${tone} ${scheme} ${rule ? '' : 'no-rule'} ${className}`}
    >
      {aurora && <div className="gx-layer gx-aurora" />}
      {pattern !== 'none' && <div className={`gx-layer gx-${pattern}`} />}
      {topo && <div className="gx-layer gx-topo" />}
      {watermark && <img src="/icons/globistaan-live-logo.png" alt="" aria-hidden="true" className="gx-watermark" />}
      {noise && <div className="gx-layer noise" />}
      {glows.map((g, i) => (
        <div
          key={i}
          className="gx-glow"
          style={{
            background: g.color,
            width: g.size || 420,
            height: g.size || 420,
            top: g.top,
            left: g.left,
            right: g.right,
            bottom: g.bottom
          }}
        />
      ))}
      <div className={`gx-content ${padding}`}>
        <div className={`gx-container ${containerClassName}`} style={{ maxWidth: contentWidth }}>
          {children}
        </div>
      </div>
    </Tag>
  );
};

export const Eyebrow = ({ children, className = '', style }) => (
  <span className={`eyebrow ${className}`} style={style}>{children}</span>
);

export const SectionHeading = ({ eyebrow, title, subtitle, align = 'center', className = '', maxWidth = 640 }) => (
  <motion.div
    {...fadeInUp}
    className={`${align === 'center' ? 'text-center mx-auto' : 'text-left'} ${className}`}
    style={{ maxWidth: align === 'center' ? maxWidth + 120 : undefined }}
  >
    {eyebrow && <div className="mb-4"><Eyebrow>{eyebrow}</Eyebrow></div>}
    <h2 className="h-section" style={{ fontSize: 'clamp(1.7rem, 3.6vw, 2.5rem)' }}>{title}</h2>
    {subtitle && (
      <p
        className={`lede mt-4 ${align === 'center' ? 'mx-auto' : ''}`}
        style={{ maxWidth: align === 'center' ? maxWidth : undefined }}
      >
        {subtitle}
      </p>
    )}
  </motion.div>
);

/** Abstract Himalayan ridgeline — the brand motif, drawn in code instead of a photograph. */
export const Ridgeline = ({ opacity = 1, height = 220, style }) => (
  <svg
    className="ridge"
    viewBox="0 0 1440 220"
    preserveAspectRatio="none"
    aria-hidden="true"
    style={{ height, opacity, ...style }}
  >
    <defs>
      <linearGradient id="ridge-far" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="rgba(167,139,250,0.28)" />
        <stop offset="1" stopColor="rgba(167,139,250,0.02)" />
      </linearGradient>
      <linearGradient id="ridge-mid" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="rgba(34,211,238,0.3)" />
        <stop offset="1" stopColor="rgba(34,211,238,0.02)" />
      </linearGradient>
      <linearGradient id="ridge-near" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="rgba(7,10,18,0.95)" />
        <stop offset="1" stopColor="rgba(7,10,18,1)" />
      </linearGradient>
    </defs>
    <path
      d="M0 160 L120 110 L220 140 L330 70 L430 120 L540 60 L640 115 L760 40 L860 100 L960 55 L1080 120 L1180 80 L1290 130 L1380 95 L1440 120 L1440 220 L0 220 Z"
      fill="url(#ridge-far)"
    />
    <path
      d="M0 185 L90 150 L200 170 L300 120 L410 165 L520 110 L620 160 L720 95 L830 150 L930 115 L1040 165 L1150 125 L1260 170 L1360 140 L1440 165 L1440 220 L0 220 Z"
      fill="url(#ridge-mid)"
    />
    <path
      d="M0 205 L110 185 L230 200 L340 170 L460 198 L580 165 L690 195 L800 160 L920 192 L1030 170 L1150 200 L1270 178 L1380 198 L1440 190 L1440 220 L0 220 Z"
      fill="url(#ridge-near)"
    />
    <polyline
      points="0,160 120,110 220,140 330,70 430,120 540,60 640,115 760,40 860,100 960,55 1080,120 1180,80 1290,130 1380,95 1440,120"
      fill="none"
      stroke="rgba(167,139,250,0.5)"
      strokeWidth="1"
    />
    <polyline
      points="0,185 90,150 200,170 300,120 410,165 520,110 620,160 720,95 830,150 930,115 1040,165 1150,125 1260,170 1360,140 1440,165"
      fill="none"
      stroke="rgba(34,211,238,0.6)"
      strokeWidth="1.2"
    />
  </svg>
);

/** Standard interior page hero. */
export const PageHero = ({
  tone = 'navy',
  eyebrow,
  title,
  subtitle,
  children,
  glows,
  pattern = 'grid',
  minHeight = '58vh',
  maxWidth = 900,
  ridge = false,
  aurora = true,
  topo = true,
  watermark = true,
  decor = null
}) => (
  <Section
    tone={tone}
    pattern={pattern}
    aurora={aurora}
    topo={topo}
    watermark={watermark}
    rule={false}
    padding="pt-40 pb-24"
    className="flex items-center"
    glows={glows || [
      { color: 'rgba(34,211,238,0.22)', top: '-10%', left: '-6%', size: 520 },
      { color: 'rgba(167,139,250,0.2)', bottom: '-20%', right: '-6%', size: 520 }
    ]}
  >
    {decor}
    <div className="text-center mx-auto relative z-10" style={{ maxWidth, minHeight: `calc(${minHeight} - 16rem)`, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {eyebrow && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-6">
          <Eyebrow>{eyebrow}</Eyebrow>
        </motion.div>
      )}
      <motion.h1
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
        className="h-display"
        style={{ fontSize: 'clamp(2.4rem, 5.6vw, 4.4rem)', marginBottom: '1.25rem' }}
      >
        {title}
      </motion.h1>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="lede mx-auto"
          style={{ maxWidth: 680 }}
        >
          {subtitle}
        </motion.p>
      )}
      {children}
    </div>
    {ridge && <Ridgeline opacity={0.9} height={160} />}
  </Section>
);
